import {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { useInfiniteQuery } from 'react-query';
import { IDistrictViewModel } from 'viewModels';
import { useAuth } from './auth';
import { TypeUser } from '../types/enums';
import { DistrictController } from '../controller/DistrictController';

type ProviderProps = {
  children: ReactNode;
};

type DistrictsContextProps = {
  districts: IDistrictViewModel[];
  setDistricts: React.Dispatch<React.SetStateAction<IDistrictViewModel[]>>;
  districtsSelectedsByUser: IDistrictViewModel[];
  setDistrictsSelectedsByUser: React.Dispatch<React.SetStateAction<IDistrictViewModel[]>>;
  isLoadingDistrictsSelectedByUser: boolean;
  fetchNextPageDistrictsSelectedByUser: () => void;
  hasNextPageDistrictsSelectedByUser: boolean | undefined;
  isFetchingNextPageDistrictsSelectedByUser: boolean;
  handleFetchDistrictsByUfCode: (ufCode: number) => Promise<IDistrictViewModel[]>;
};

const DistrictsContext = createContext<DistrictsContextProps>({} as DistrictsContextProps);

const DistrictsProvider = ({ children }: ProviderProps) => {
  const TIMER_TO_FETCH_NEXT_PAGE = 500;
  const PAGE_SIZE = 10;
  const { tokenIsValid, user } = useAuth();
  const [districts, setDistricts] = useState<IDistrictViewModel[]>([] as IDistrictViewModel[]);
  const [districtsSelectedsByUser, setDistrictsSelectedsByUser] = useState<IDistrictViewModel[]>(
    [] as IDistrictViewModel[],
  );
  const {
    isLoading: isLoadingDistrictsSelectedByUser,
    fetchNextPage: fetchNextPageDistrictsSelectedByUser,
    hasNextPage: hasNextPageDistrictsSelectedByUser,
    isFetchingNextPage: isFetchingNextPageDistrictsSelectedByUser,
  } = useInfiniteQuery(
    'district-selected-by-user',
    ({ pageParam = 1 }) =>
      DistrictController.getDistrictSelectByUser({ take: PAGE_SIZE, page: pageParam }),
    {
      onSuccess: (res) => {
        setDistrictsSelectedsByUser(res.pages?.flat() ?? []);
      },
      getNextPageParam: (lastPage, allPages) => {
        const nextPage = allPages.length + 1;
        return lastPage.length < PAGE_SIZE ? undefined : nextPage;
      },
      refetchOnWindowFocus: false,
      enabled: tokenIsValid && user.type !== TypeUser.COLLABORATOR,
    },
  );

  const handleFetchDistrictsByUfCode = useCallback(
    async (ufCode: number) => {
      const alreadyFetchedAllByUfCode = districts.some((district) => district.ufCode === ufCode);
      if (!alreadyFetchedAllByUfCode) {
        const response = await DistrictController.getDistrictsByUfCode(ufCode).then((res) => {
          setDistricts((prev) => {
            const newDistricts = [...prev];
            res.forEach((district) => {
              if (!newDistricts.some((d) => d.id === district.id)) {
                newDistricts.push(district);
              }
            });
            return newDistricts;
          });
          return res;
        });
        return response;
      }
      return districts.filter((district) => district.ufCode === ufCode);
    },
    [districts],
  );

  useEffect(() => {
    if (!tokenIsValid) {
      setDistricts([]);
      setDistrictsSelectedsByUser([]);
    }
  }, [tokenIsValid]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (hasNextPageDistrictsSelectedByUser) {
      interval = setInterval(() => {
        fetchNextPageDistrictsSelectedByUser();
      }, TIMER_TO_FETCH_NEXT_PAGE);
    }
    return () => clearInterval(interval);
  }, [fetchNextPageDistrictsSelectedByUser, hasNextPageDistrictsSelectedByUser]);

  const contextValues = useMemo(
    () => ({
      districts,
      setDistricts,
      districtsSelectedsByUser,
      setDistrictsSelectedsByUser,
      isLoadingDistrictsSelectedByUser,
      fetchNextPageDistrictsSelectedByUser,
      hasNextPageDistrictsSelectedByUser,
      isFetchingNextPageDistrictsSelectedByUser,
      handleFetchDistrictsByUfCode,
    }),
    [
      districts,
      districtsSelectedsByUser,
      isLoadingDistrictsSelectedByUser,
      fetchNextPageDistrictsSelectedByUser,
      hasNextPageDistrictsSelectedByUser,
      isFetchingNextPageDistrictsSelectedByUser,
      handleFetchDistrictsByUfCode,
    ],
  );
  return <DistrictsContext.Provider value={contextValues}>{children}</DistrictsContext.Provider>;
};

export default DistrictsProvider;

export const useDistricts: () => DistrictsContextProps = () => useContext(DistrictsContext);
